import { details } from "domain/endpoint";
import { apiGatewayService, ApiType } from "services/api/ApiGatewayService";
import { ParameterQuery } from "utils/commonFunctions";
import { logger } from "utils/logging";

interface PasswordResetDto {
    username: string;
    password: string;
    code?: string;
    temporary_password?: string;
}

interface OAuthFinishDto {
    code: string;
    redirect_uri: string;
    state?: string;
}

export interface OAuthFinishResult {
    code: string;
    redirectUri: string;
    state?: string;
}

function toOAuthFinishResult(dto: OAuthFinishDto): OAuthFinishResult {
    return {
        code: dto.code,
        redirectUri: dto.redirect_uri,
        state: dto.state,
    };
}

/**
 * Handles interactions with authentication API.
 */
class AuthenticationService {
    public async authenticate(username: string, password: string): Promise<details> {
        try {
            const requestBody = {
                username,
                password,
            };

            const response: details = await apiGatewayService.invokeApi("/authentication", "post", requestBody, {
                refreshSession: false,
                apiType: ApiType.GLOBAL_STAN,
            });
            return Promise.resolve(response);
        } catch (e) {
            return Promise.reject(e);
        }
    }

    public async ssoAuthenticate(authorizationCode: string): Promise<details> {
        try {
            const response: details = await apiGatewayService.invokeApi(
                "/authentication/sso?code=" + authorizationCode,
                "get",
                undefined,
                {
                    refreshSession: false,
                    apiType: ApiType.GLOBAL_STAN,
                }
            );
            return Promise.resolve(response);
        } catch (e) {
            return Promise.reject(e);
        }
    }

    public async requestPasswordReset(email: string): Promise<void> {
        return apiGatewayService.invokeApi("/forgot-password?username=" + encodeURIComponent(email), "get", null);
    }

    public passwordReset(
        email: string,
        verificationCode: string | null,
        temporaryPassword: string | null,
        newPassword: string
    ): Promise<void> {
        const body: PasswordResetDto = {
            username: email,
            password: newPassword,
        };
        if (verificationCode != null) {
            body["code"] = verificationCode;
        } else if (temporaryPassword != null && temporaryPassword != "") {
            body["temporary_password"] = temporaryPassword;
        }
        return apiGatewayService.invokeApi(`/reset-password`, "post", body, {
            refreshSession: false,
            emptyResponse: true,
        });
    }

    public async validateTemporaryPassword(
        email: string,
        isTemporaryPassword: boolean,
        sentTimestamp: string
    ): Promise<void> {
        const query = new ParameterQuery()
            .add("username", email)
            .add("isTemporaryPassword", String(isTemporaryPassword))
            .add("sentTimestamp", sentTimestamp)
            .createQueryString();
        return apiGatewayService.invokeApi(`/api/v1/passwords/validate${query}`, "get", null, {
            refreshSession: false,
            emptyResponse: true,
            apiType: ApiType.ARTHUR,
        });
    }

    public updatePassword(oldPassword: string, updatePassword: string): Promise<void> {
        const body: { [key: string]: string } = {
            old_password: oldPassword,
            new_password: updatePassword,
        };
        return apiGatewayService.invokeApi("/api/v1/users/update-passwords", "post", body, {
            refreshSession: false,
            apiType: ApiType.LAUREL,
            emptyResponse: true,
        });
    }

    public async oauthFinish(oauthState: string): Promise<OAuthFinishResult> {
        const query = new ParameterQuery().add("oauth_state", oauthState).createQueryString();
        const dto: OAuthFinishDto = await apiGatewayService.invokeApi(
            `/api/v1/authentication/oauth-finish${query}`,
            "get",
            null,
            {
                refreshSession: false,
                apiType: ApiType.JEFFERSON,
            }
        );
        return toOAuthFinishResult(dto);
    }

    public async logout() {
        try {
            await apiGatewayService.invokeApi("/logout", "get");
        } catch (e) {
            logger.error("Logout failed: " + e);
        }
    }
}

export const authenticationService = new AuthenticationService();
